<template>
  <div class="jurisdiction-report-page">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.compliance.jurisdiction_report.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.compliance.jurisdiction_report.subtitle') }}</p>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-bar">
      <input
        v-model="filters.country_code"
        type="text"
        class="filter-input filter-input--sm"
        placeholder="Country (e.g. US)"
        maxlength="2"
        @keyup.enter="fetchReport"
      />
      <select v-model="filters.event_type" class="filter-select" @change="fetchReport">
        <option value="">{{ $t('admin.compliance.jurisdiction_report.filter_event_all') }}</option>
        <option value="login">{{ $t('admin.compliance.jurisdiction_report.filter_event_login') }}</option>
        <option value="deposit">{{ $t('admin.compliance.jurisdiction_report.filter_event_deposit') }}</option>
        <option value="bet">{{ $t('admin.compliance.jurisdiction_report.filter_event_bet') }}</option>
        <option value="withdrawal">{{ $t('admin.compliance.jurisdiction_report.filter_event_withdrawal') }}</option>
      </select>
      <input v-model="filters.start_date" type="date" class="filter-input" @change="fetchReport" />
      <span class="date-sep">{{ $t('admin.compliance.jurisdiction_report.filter_to') }}</span>
      <input v-model="filters.end_date" type="date" class="filter-input" @change="fetchReport" />
      <input
        v-model="filters.user_id"
        type="text"
        class="filter-input filter-input--sm"
        placeholder="User ID"
        @keyup.enter="fetchReport"
      />
      <button class="btn btn-primary" :disabled="loading" @click="fetchReport">
        <span v-if="loading" class="spinner spinner-sm"></span>
        {{ $t('admin.compliance.jurisdiction_report.btn_apply') }}
      </button>
    </div>

    <!-- Report Card -->
    <div class="report-card">
      <div class="report-header">
        <div class="report-title">{{ $t('admin.compliance.jurisdiction_report.section_title') }}</div>
        <div v-if="report" class="report-period">
          {{ report.date_range.start }} — {{ report.date_range.end }}
        </div>
      </div>

      <div v-if="loading" class="report-loading">
        <span class="spinner"></span>
        {{ $t('admin.compliance.jurisdiction_report.loading') }}
      </div>

      <div v-else-if="!report || report.data.length === 0" class="report-empty">
        {{ $t('admin.compliance.jurisdiction_report.empty') }}
      </div>

      <table v-else class="report-table">
        <thead>
          <tr>
            <th>{{ $t('admin.compliance.jurisdiction_report.col_country') }}</th>
            <th class="text-right">{{ $t('admin.compliance.jurisdiction_report.col_logins') }}</th>
            <th class="text-right">{{ $t('admin.compliance.jurisdiction_report.col_deposits') }}</th>
            <th class="text-right">{{ $t('admin.compliance.jurisdiction_report.col_bets') }}</th>
            <th class="text-right">{{ $t('admin.compliance.jurisdiction_report.col_withdrawals') }}</th>
            <th class="text-right">{{ $t('admin.compliance.jurisdiction_report.col_unique_players') }}</th>
            <th class="text-right">{{ $t('admin.compliance.jurisdiction_report.col_total_events') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in report.data"
            :key="row.country_code"
            :class="{ 'row--blocked': row.is_blocked }"
          >
            <td class="country-cell">
              <span class="country-flag" aria-hidden="true">{{ countryFlag(row.country_code) }}</span>
              <span class="country-code">{{ row.country_code }}</span>
              <span v-if="row.is_blocked" class="badge badge-red">{{ $t('admin.compliance.jurisdiction_report.badge_blocked') }}</span>
            </td>
            <td class="text-right mono">{{ row.logins.toLocaleString() }}</td>
            <td class="text-right mono">{{ row.deposits.toLocaleString() }}</td>
            <td class="text-right mono">{{ row.bets.toLocaleString() }}</td>
            <td class="text-right mono">{{ row.withdrawals.toLocaleString() }}</td>
            <td class="text-right mono">{{ row.unique_players.toLocaleString() }}</td>
            <td class="text-right mono text-bold">{{ row.total_events.toLocaleString() }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()
const toast = useToastStore()

// ----------- Types -----------

interface JurisdictionRow {
  country_code: string
  logins: number
  deposits: number
  bets: number
  withdrawals: number
  unique_players: number
  total_events: number
  is_blocked: boolean
}

interface JurisdictionReport {
  data: JurisdictionRow[]
  blocked_countries: string[]
  date_range: {
    start: string
    end: string
  }
}

// ----------- State -----------

const loading = ref(false)
const report = ref<JurisdictionReport | null>(null)

const defaultEnd = new Date()
const defaultStart = new Date()
defaultStart.setDate(defaultStart.getDate() - 30)

const filters = ref({
  country_code: '',
  event_type: '',
  start_date: defaultStart.toISOString().split('T')[0],
  end_date: defaultEnd.toISOString().split('T')[0],
  user_id: '',
})

// ----------- Helpers -----------

function countryFlag(code: string): string {
  return code
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('')
}

// ----------- Data fetching -----------

async function fetchReport(): Promise<void> {
  loading.value = true
  try {
    const params: Record<string, string> = {
      start_date: filters.value.start_date,
      end_date: filters.value.end_date,
    }

    if (filters.value.country_code.trim()) {
      params.country_code = filters.value.country_code.trim().toUpperCase()
    }
    if (filters.value.event_type) {
      params.event_type = filters.value.event_type
    }
    if (filters.value.user_id.trim()) {
      params.user_id = filters.value.user_id.trim()
    }

    const response = await apiClient.get('/admin/api/reports/jurisdiction', { params })
    report.value = response.data as JurisdictionReport
  } catch {
    toast.error(t('admin.compliance.jurisdiction_report.error_load'))
  } finally {
    loading.value = false
  }
}

// ----------- Lifecycle -----------

onMounted(async () => {
  await fetchReport()
})
</script>

<style scoped>
.jurisdiction-report-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

/* Filters */
.filters-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 16px;
}

.filter-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 8px 12px;
  font-size: 13px;
  font-family: inherit;
  min-width: 140px;
}

.filter-input--sm {
  min-width: 100px;
}

.filter-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.filter-select {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 8px 12px;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  min-width: 150px;
}

.filter-select:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.date-sep {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #1475e1;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: #1a85f5;
}

/* Report Card */
.report-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  overflow-x: auto;
}

.report-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.report-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.report-period {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.report-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

.report-empty {
  padding: 40px;
  text-align: center;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

/* Table */
.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.report-table th {
  text-align: left;
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.report-table th.text-right {
  text-align: right;
}

.report-table td {
  padding: 12px;
  color: var(--text-secondary, #b1bad3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  vertical-align: middle;
}

.report-table tr:last-child td {
  border-bottom: none;
}

.report-table tr.row--blocked {
  background: rgba(239, 83, 80, 0.05);
}

.report-table tr.row--blocked:hover {
  background: rgba(239, 83, 80, 0.08);
}

.report-table tr:not(.row--blocked):hover {
  background: rgba(255, 255, 255, 0.02);
}

/* Country Cell */
.country-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.country-flag {
  font-size: 18px;
  line-height: 1;
}

.country-code {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-weight: 600;
  font-size: 13px;
  color: var(--text-primary, #fff);
  letter-spacing: 0.05em;
}

/* Alignment & formatting */
.text-right {
  text-align: right;
}

.mono {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-weight: 500;
}

.text-bold {
  font-weight: 700;
  color: var(--text-primary, #fff);
}

/* Badge */
.badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.badge-red {
  background: rgba(239, 83, 80, 0.15);
  color: #ef5350;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-sm {
  width: 12px;
  height: 12px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .jurisdiction-report-page {
    padding: 16px;
  }

  .filters-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-input,
  .filter-select {
    min-width: 100%;
  }
}
</style>
